import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { BreadcrumbProps as AntDBreadcrumbProps } from 'antd';
import { isNil } from 'lodash';
import { useRouter } from 'next/router';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../colors-v2';
import { Link } from '../Link';
import { ResponsiveTypography } from '../Typography/v3/ResponsiveTypography';
import { PageTypeForLandingBlogAsPagePath } from '@doltech/utils/lib/constants';

const Main = styled.div.withConfig({
  componentId: 'Breadcrumb_Main',
} as any)`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  transition: all 0.2s ease;
  text-transform: uppercase;

  .breadcrumb-item {
    display: inline-block;
    margin-bottom: 0;
    color: ${colorsV2.primary100};
    padding: 4px;
    &:hover {
      text-decoration: underline;
    }
  }

  .dol-breadcrumb-separator {
    margin: 0 4px;
    display: inline-block;
    color: ${colorsV2.primary100};
    font-size: 16px;
    line-height: 0;
  }
`;

const IconWrapper = styled.i.withConfig({
  componentId: 'Breadcrumb_IconWrapper',
} as any)`
  top: -6px;
  position: relative;
  color: ${colorsV2.gray100};
  line-height: 0;
`;

interface BreadcrumbProps extends AntDBreadcrumbProps {
  useFor?: 'landing-blog' | 'landing' | '';
  seoData?: any;
}

const BreadcrumbBase = (props: BreadcrumbProps) => {
  const { useFor, routes, seoData, ...rest } = props;
  const { url, dol, type } = seoData || {};
  const currentUrl = getLandingUrlFromPath(url, useFor);
  const router = useRouter();

  const breadcrumbListSchema = React.useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${currentUrl}#breadcrumb`,
      itemListElement: [
        ...routes.map(({ breadcrumbName, path }, routeIndex) => {
          if (routeIndex === 0) {
            return {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'WebPage',
                '@id': 'https://www.dolenglish.vn',
                url: 'https://www.dolenglish.vn',
                name: 'Trang chủ',
              },
            };
          }
          return {
            '@type': 'ListItem',
            position: routeIndex + 1,
            item: {
              '@id': getLandingUrlFromPath(path, useFor),
              url: getLandingUrlFromPath(path, useFor),
              name: breadcrumbName,
            },
          };
        }),
        PageTypeForLandingBlogAsPagePath.includes(type) && {
          '@type': 'ListItem',
          position: routes.length + 1,
          item: {
            '@id': currentUrl,
            url: currentUrl,
            name: dol?.title,
          },
        },
      ],
    }),
    [routes, type, dol, currentUrl, useFor]
  );

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.innerHTML = JSON.stringify(breadcrumbListSchema);
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [breadcrumbListSchema]);

  return (
    <Main className="dol-breadcrumb" {...rest}>
      {routes.map((route, routeIndex) => {
        const { breadcrumbName, path } = route;
        const isCurrentPath =
          `${router.basePath}${router.asPath}` === path ||
          router?.asPath === path ||
          isNil(path) ||
          path === '#';
        const isShowSeparator = routeIndex < routes.length - 1;
        return (
          <React.Fragment key={breadcrumbName + path}>
            {isCurrentPath ? (
              <div className="breadcrumb-item">
                <ResponsiveTypography.Text ellipsis={{ tooltip: breadcrumbName }}>
                  {breadcrumbName}
                </ResponsiveTypography.Text>
              </div>
            ) : (
              <Link className="breadcrumb-item" href={getLandingUrlFromPath(path, useFor)}>
                <ResponsiveTypography.Text ellipsis={{ tooltip: breadcrumbName }}>
                  {breadcrumbName}
                </ResponsiveTypography.Text>
              </Link>
            )}
            {isShowSeparator && (
              <IconWrapper>
                <IconChevronChevronRight className="dol-breadcrumb-separator" />
              </IconWrapper>
            )}
          </React.Fragment>
        );
      })}
    </Main>
  );
};

export const Breadcrumb = BreadcrumbBase;
